import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding, faChartBar, faChartPie, faCheckCircle, faFilter,
  faSearch, faTrophy, faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import DotLoader from "../../components/Ui_Element/DotLoader";
import { showToast } from "../../components/Ui_Element/CustomToast";
import {
  fetchDepartmentTaskAnalytics,
  fetchDepartmentsForFilter,
  getDefaultDeptTaskFilters,
} from "./departmentTaskAnalyticsService";

const BRAND_135 = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);
const MONTHS = [
  { value: "1", label: "January" }, { value: "2", label: "February" },
  { value: "3", label: "March" }, { value: "4", label: "April" },
  { value: "5", label: "May" }, { value: "6", label: "June" },
  { value: "7", label: "July" }, { value: "8", label: "August" },
  { value: "9", label: "September" }, { value: "10", label: "October" },
  { value: "11", label: "November" }, { value: "12", label: "December" },
];

const STATUS_COLORS = {
  completed: "#22c55e",
  pending: "#f59e0b",
  inProgress: "#3b82f6",
  cancelled: "#ef4444",
  waiting: "#8b5cf6",
};

function SummaryCard({ icon, label, value, sub, color }) {
  return (
    <div className="min-w-0 rounded-2xl border bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-[#161B22] sm:p-4" style={{ borderColor: "rgba(192,76,42,0.12)" }}>
      <div className="mb-2 flex items-center gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-sm text-white" style={{ background: color || BRAND_135 }}>
          <FontAwesomeIcon icon={icon} />
        </div>
        <span className="min-w-0 truncate text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">{label}</span>
      </div>
      <div className="truncate text-2xl font-extrabold text-slate-900 dark:text-white">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-slate-400 dark:text-gray-500">{sub}</div>}
    </div>
  );
}

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload[0]?.payload?.Total;
  return (
    <div className="rounded-xl border border-orange-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-lg text-xs">
      <p className="mb-1 font-bold text-slate-700 dark:text-gray-200">{label}</p>
      {Number.isFinite(total) && (
        <p className="mb-1 font-bold" style={{ color: "#C04C2A" }}>Total: {total}</p>
      )}
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.fill || p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

export default function DepartmentTaskAnalyticsPage({ embedded = false }) {
  const [filters, setFilters] = useState(getDefaultDeptTaskFilters);
  const [departments, setDepartments] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [error, setError] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setFiltersLoading(true);
      setLoading(true);
      const depts = await fetchDepartmentsForFilter();
      if (!cancelled) {
        setDepartments(depts);
        setFiltersLoading(false);
      }
      try {
        const result = await fetchDepartmentTaskAnalytics(getDefaultDeptTaskFilters());
        if (!cancelled) setReport(result);
      } catch {
        if (!cancelled) setError("Could not load initial report.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  const runReport = useCallback(async (activeFilters) => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchDepartmentTaskAnalytics(activeFilters);
      setReport(result);
      return true;
    } catch (err) {
      const msg = err?.message || "Report could not be loaded.";
      setError(msg);
      showToast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRun = async () => {
    const ok = await runReport(filters);
    if (ok) setPanelOpen(false);
  };

  const handleReset = async () => {
    const next = getDefaultDeptTaskFilters();
    setFilters(next);
    setError("");
    const ok = await runReport(next);
    if (ok) setPanelOpen(false);
  };

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const deptStats = report?.departmentStats ?? [];
  const summary = report?.summary ?? {};
  const trend = report?.monthlyTrend ?? [];

  const barData = deptStats.map((d) => ({
    name: d.departmentName,
    Total: d.total,
    Completed: d.completed,
    Pending: d.pending,
    "In Progress": d.inProgress,
    Waiting: d.waiting,
    Cancelled: d.cancelled,
  }));

  const pieData = [
    { name: "Completed", value: summary.completedTasks || 0, color: STATUS_COLORS.completed },
    { name: "Pending", value: summary.pendingTasks || 0, color: STATUS_COLORS.pending },
    { name: "In Progress", value: summary.inProgressTasks || 0, color: STATUS_COLORS.inProgress },
    { name: "Cancelled", value: summary.cancelledTasks || 0, color: STATUS_COLORS.cancelled },
  ].filter((d) => d.value > 0);

  const lineData = trend.map((t) => ({
    name: t.monthLabel,
    Total: t.departments.reduce((s, d) => s + d.total, 0),
    Completed: t.departments.reduce((s, d) => s + d.completed, 0),
  }));

  const filterLabel = filters.mode === "month" && filters.month
    ? `${MONTHS.find((m) => m.value === filters.month)?.label} ${filters.year}`
    : filters.mode === "custom" && filters.startDate && filters.endDate
      ? `${filters.startDate} → ${filters.endDate}`
      : `Year ${filters.year}`;

  return (
    <div className={embedded ? "" : "min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 dark:bg-none dark:bg-[#0F111A] p-2 transition-colors duration-300 sm:p-3 md:p-5"}>
      {!embedded && <Breadcrumb pageName="Department Task Analytics" currentPage="Department Task Analytics" />}

      {/* ── Hero Header ── */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-xl dark:border-gray-800 dark:bg-[#161B22] sm:rounded-3xl">
        <div className="relative overflow-hidden px-3 py-5 text-white sm:px-5 md:px-7 md:py-8" style={{ background: BRAND_135 }}>
          <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/5 blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 left-1/4 h-24 w-24 rounded-full blur-2xl" style={{ background: "rgba(255,160,100,0.12)" }} />

          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0 max-w-2xl">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-orange-100">
                <FontAwesomeIcon icon={faChartBar} />
                Department Task Analytics
              </div>
              <h1 className="mt-2 text-base font-extrabold tracking-tight text-white sm:text-xl">
                Workload &amp; Performance by Department
              </h1>
              <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                <span className="rounded-full border border-white/15 bg-white/10 px-2.5 py-0.5 text-orange-100">{filterLabel}</span>
                {filters.departmentId && (
                  <span className="rounded-full border border-white/15 bg-white/10 px-2.5 py-0.5 text-orange-100">
                    {departments.find((d) => String(d.id) === String(filters.departmentId))?.name ?? "Dept"}
                  </span>
                )}
              </div>
            </div>

            <div className="grid w-full grid-cols-1 gap-2 min-[420px]:grid-cols-2 lg:w-auto">
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/20 bg-white/15 px-3 py-2 text-xs font-bold text-white transition hover:bg-white/25"
              >
                <FontAwesomeIcon icon={faFilter} /> Filters
              </button>
              <button
                type="button"
                onClick={handleRun}
                disabled={loading || filtersLoading}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? <DotLoader color="#fff" /> : <FontAwesomeIcon icon={faSearch} />}
                {loading ? "Loading…" : "Run Report"}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-5 p-3 sm:p-4 md:p-6">
          {error && (
            <div className="rounded-xl border border-rose-200 dark:border-red-800 bg-rose-50 dark:bg-red-900/20 px-4 py-2.5 text-sm text-rose-700 dark:text-red-400">{error}</div>
          )}

          {loading ? (
            <div className="rounded-2xl border border-dashed border-orange-200 dark:border-gray-700 bg-orange-50/30 dark:bg-gray-800/30 px-6 py-14 text-center text-sm" style={{ color: "#C04C2A" }}>
              <DotLoader size="w-3 h-3" />
              <p className="mt-3 font-semibold">Loading department analytics...</p>
            </div>
          ) : report ? (
            <>
              {/* ── Summary Cards ── */}
              <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
                <SummaryCard icon={faChartBar} label="Total Tasks" value={summary.totalTasks} sub={`${summary.totalDepartments} departments`} />
                <SummaryCard icon={faCheckCircle} label="Completed" value={summary.completedTasks} sub={`${summary.completionRate}% rate`} color="linear-gradient(135deg,#22c55e,#16a34a)" />
                <SummaryCard icon={faFilter} label="Pending" value={summary.pendingTasks} color="linear-gradient(135deg,#f59e0b,#d97706)" />
                <SummaryCard icon={faTrophy} label="Most Loaded" value={summary.highestDepartment || "—"} sub={`${summary.highestDepartmentCount} tasks`} />
                <SummaryCard icon={faBuilding} label="Least Loaded" value={summary.lowestDepartment || "—"} sub={`${summary.lowestDepartmentCount} tasks`} color="linear-gradient(135deg,#8b5cf6,#6d28d9)" />
              </div>

              {/* ── Bar Chart ── */}
              <div className="min-w-0 rounded-2xl border bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-[#161B22] sm:p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faChartBar} style={{ color: "#C04C2A" }} />
                    <h2 className="text-sm font-bold text-slate-800 dark:text-gray-200">Department-wise Task Volume</h2>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="rounded-full border border-orange-200 dark:border-gray-700 bg-orange-50 dark:bg-gray-800 px-2.5 py-0.5 text-[10px] font-bold" style={{ color: "#C04C2A" }}>
                      {barData.length} Departments
                    </span>
                    <span className="rounded-full border border-orange-200 dark:border-gray-700 bg-orange-50 dark:bg-gray-800 px-2.5 py-0.5 text-[10px] font-bold" style={{ color: "#C04C2A" }}>
                      {summary.totalTasks} Tasks
                    </span>
                    {summary.highestDepartment ? (
                      <span className="rounded-full border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-400">
                        <FontAwesomeIcon icon={faTrophy} className="mr-1" />
                        Top: {summary.highestDepartment} ({summary.highestDepartmentCount})
                      </span>
                    ) : null}
                  </div>
                </div>
                {barData.length ? (
                  <div className="-mx-2 overflow-x-auto px-2">
                    <div className="min-w-[680px]">
                      <ResponsiveContainer width="100%" height={Math.max(320, barData.length * 52 + 60)}>
                        <BarChart data={barData} layout="vertical" barSize={24} margin={{ top: 5, right: 56, left: 8, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11, fill: "#9ca3af" }} allowDecimals={false} />
                          <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12, fill: "#d1d5db", fontWeight: 600 }} interval={0} />
                          <Tooltip content={<CustomBarTooltip />} />
                          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                          <Bar dataKey="Completed" stackId="status" fill={STATUS_COLORS.completed} />
                          <Bar dataKey="In Progress" stackId="status" fill={STATUS_COLORS.inProgress} />
                          <Bar dataKey="Pending" stackId="status" fill={STATUS_COLORS.pending} />
                          <Bar dataKey="Waiting" stackId="status" fill={STATUS_COLORS.waiting} />
                          <Bar dataKey="Cancelled" stackId="status" fill={STATUS_COLORS.cancelled} radius={[0, 4, 4, 0]}>
                            <LabelList dataKey="Total" position="right" style={{ fontSize: 11, fontWeight: 700, fill: "#C04C2A" }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <p className="py-10 text-center text-sm text-slate-400 dark:text-gray-500">No department data available.</p>
                )}
              </div>

              {/* ── Pie + Line ── */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="min-w-0 rounded-2xl border bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-[#161B22] sm:p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <FontAwesomeIcon icon={faChartPie} style={{ color: "#C04C2A" }} />
                    <h2 className="text-sm font-bold text-slate-800 dark:text-gray-200">Task Status Distribution</h2>
                  </div>
                  {pieData.length ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                          {pieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ background: "#1f2937", borderColor: "#374151", color: "#fff", borderRadius: 12, fontSize: 12 }} labelStyle={{ color: "#fff", fontWeight: 700 }} itemStyle={{ color: "#fff" }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="py-10 text-center text-sm text-slate-400 dark:text-gray-500">No status data available.</p>
                  )}
                </div>

                <div className="min-w-0 rounded-2xl border bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-[#161B22] sm:p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <FontAwesomeIcon icon={faChartBar} style={{ color: "#C04C2A" }} />
                    <h2 className="text-sm font-bold text-slate-800 dark:text-gray-200">Monthly Task Trend</h2>
                  </div>
                  {lineData.length ? (
                    <div className="-mx-2 overflow-x-auto px-2">
                      <div className="min-w-[560px]">
                        <ResponsiveContainer width="100%" height={260}>
                          <LineChart data={lineData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                            <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} allowDecimals={false} />
                            <Tooltip contentStyle={{ background: "#1f2937", borderColor: "#374151", color: "#fff", borderRadius: 12, fontSize: 12 }} labelStyle={{ color: "#fff", fontWeight: 700 }} itemStyle={{ color: "#fff" }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="Total" stroke="#C04C2A" strokeWidth={2} dot={{ r: 3 }} />
                            <Line type="monotone" dataKey="Completed" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <p className="py-10 text-center text-sm text-slate-400 dark:text-gray-500">No trend data available.</p>
                  )}
                </div>
              </div>

              {/* ── Department Detail Table ── */}
              <div className="rounded-2xl border bg-white dark:bg-[#161B22] dark:border-gray-700 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b dark:border-gray-700" style={{ background: BRAND_135 }}>
                  <h2 className="text-sm font-bold text-white flex items-center gap-2">
                    <FontAwesomeIcon icon={faBuilding} /> Department Performance Breakdown
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead>
                      <tr className="border-b border-orange-100 dark:border-gray-700 bg-orange-50/50 dark:bg-gray-800/50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-400">
                        <th className="px-4 py-2.5">Department</th>
                        <th className="px-4 py-2.5 text-center">Total</th>
                        <th className="px-4 py-2.5 text-center">Completed</th>
                        <th className="px-4 py-2.5 text-center">Pending</th>
                        <th className="px-4 py-2.5 text-center">In Progress</th>
                        <th className="px-4 py-2.5 text-center">Waiting</th>
                        <th className="px-4 py-2.5 text-center">Cancelled</th>
                        <th className="px-4 py-2.5 text-center">Completion %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deptStats.length ? deptStats.map((d, i) => (
                        <tr key={d.departmentId} className={`border-b border-orange-50 dark:border-gray-800 transition hover:bg-orange-50/40 dark:hover:bg-gray-800 ${i % 2 === 0 ? "bg-white dark:bg-[#161B22]" : "bg-orange-50/20 dark:bg-gray-800/30"}`}>
                          <td className="px-4 py-2.5 font-semibold text-slate-800 dark:text-gray-200">
                            <div>{d.departmentName}</div>
                            <div className="text-[10px] text-slate-400 dark:text-gray-500">{d.departmentCode}</div>
                          </td>
                          <td className="px-4 py-2.5 text-center font-bold" style={{ color: "#C04C2A" }}>{d.total}</td>
                          <td className="px-4 py-2.5 text-center text-green-600 font-semibold">{d.completed}</td>
                          <td className="px-4 py-2.5 text-center text-amber-600 font-semibold">{d.pending}</td>
                          <td className="px-4 py-2.5 text-center text-blue-600 font-semibold">{d.inProgress}</td>
                          <td className="px-4 py-2.5 text-center text-purple-600 font-semibold">{d.waiting}</td>
                          <td className="px-4 py-2.5 text-center text-rose-600 font-semibold">{d.cancelled}</td>
                          <td className="px-4 py-2.5 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <div className="h-1.5 w-16 rounded-full bg-slate-100 dark:bg-gray-700 overflow-hidden">
                                <div className="h-full rounded-full bg-green-500" style={{ width: `${d.completionRate}%` }} />
                              </div>
                              <span className="text-[11px] font-semibold text-slate-600 dark:text-gray-300">{d.completionRate}%</span>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={8} className="py-10 text-center text-sm text-slate-400 dark:text-gray-500">No department data found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-dashed dark:border-gray-700 px-6 py-14 text-center text-sm dark:bg-gray-800/20 dark:text-gray-400" style={{ borderColor: "rgba(192,76,42,0.22)", background: "rgba(192,76,42,0.02)", color: "#8B2A15" }}>
              Run the report to view department task analytics.
            </div>
          )}
        </div>
      </div>

      {/* ── Filter Slide Panel ── */}
      <div className={`fixed inset-0 z-[80] ${panelOpen ? "" : "pointer-events-none"}`}>
        <button
          type="button"
          aria-label="Close filters"
          onClick={() => setPanelOpen(false)}
          className={`absolute inset-0 bg-slate-950/35 transition ${panelOpen ? "opacity-100" : "opacity-0"}`}
        />
        <div className={`absolute right-0 top-0 h-full w-full max-w-sm transform p-2 transition duration-300 sm:p-4 ${panelOpen ? "translate-x-0" : "translate-x-full"}`}>
          <div ref={panelRef} className="h-full overflow-y-auto rounded-[28px] bg-white dark:bg-gray-900 shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-[28px] px-5 py-4" style={{ background: BRAND_135 }}>
              <div className="flex items-center gap-2 text-sm font-bold text-white">
                <FontAwesomeIcon icon={faFilter} /> Report Filters
              </div>
              <button type="button" onClick={() => setPanelOpen(false)} className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25">
                <FontAwesomeIcon icon={faXmark} className="text-xs" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-gray-400">Filter Mode</label>
                <div className="grid grid-cols-3 gap-2">
                  {["year", "month", "custom"].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setFilter("mode", m)}
                      className="flex-1 rounded-lg border py-1.5 text-xs font-semibold capitalize transition"
                      style={{
                        background: filters.mode === m ? "#C04C2A" : "",
                        color: filters.mode === m ? "#fff" : "#64748b",
                        borderColor: filters.mode === m ? "#C04C2A" : "#e2e8f0",
                      }}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              {(filters.mode === "year" || filters.mode === "month") && (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-gray-400">Year</label>
                  <select
                    value={filters.year}
                    onChange={(e) => setFilter("year", e.target.value)}
                    className="w-full rounded-xl border border-orange-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-700 dark:text-white outline-none"
                    onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
                    onBlur={(e) => { e.target.style.borderColor = ""; }}
                  >
                    {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              )}

              {filters.mode === "month" && (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-gray-400">Month</label>
                  <select
                    value={filters.month}
                    onChange={(e) => setFilter("month", e.target.value)}
                    className="w-full rounded-xl border border-orange-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-700 dark:text-white outline-none"
                    onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
                    onBlur={(e) => { e.target.style.borderColor = ""; }}
                  >
                    <option value="">All Months</option>
                    {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              )}

              {filters.mode === "custom" && (
                <>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-gray-400">Start Date</label>
                    <input
                      type="date"
                      value={filters.startDate || ""}
                      onChange={(e) => setFilter("startDate", e.target.value)}
                      className="w-full rounded-xl border border-orange-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-700 dark:text-white outline-none"
                      onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
                      onBlur={(e) => { e.target.style.borderColor = ""; }}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-gray-400">End Date</label>
                    <input
                      type="date"
                      value={filters.endDate || ""}
                      onChange={(e) => setFilter("endDate", e.target.value)}
                      className="w-full rounded-xl border border-orange-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-700 dark:text-white outline-none"
                      onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
                      onBlur={(e) => { e.target.style.borderColor = ""; }}
                    />
                  </div>
                </>
              )}

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-gray-400">Department</label>
                <select
                  value={filters.departmentId}
                  onChange={(e) => setFilter("departmentId", e.target.value)}
                  className="w-full rounded-xl border border-orange-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-700 dark:text-white outline-none"
                  onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
                  onBlur={(e) => { e.target.style.borderColor = ""; }}
                >
                  <option value="">All Departments</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex-1 rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-2.5 text-sm font-semibold text-slate-600 dark:text-gray-300 transition hover:bg-slate-50 dark:hover:bg-gray-700"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleRun}
                  disabled={loading}
                  className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white transition disabled:opacity-60"
                  style={{ background: BRAND_135 }}
                >
                  {loading ? <DotLoader color="#fff" /> : "Run Report"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
